/*
 * Auth actions, ported from the auth state machine in taskpane.js.
 *
 * The old `transitionTo` did imperative DOM work + data loading. Here the
 * actions only mutate the store's `authState` (+ related data); the AccountPanel
 * sub-views render declaratively from that state. Async actions read fresh
 * values via useStore.getState() to avoid stale closures.
 */

import { useCallback } from "react";
import {
  browserSupportsWebAuthn,
  startAuthentication,
  WebAuthnError,
  type AuthenticationResponseJSON,
} from "@simplewebauthn/browser";
import { useStore } from "../../state/store";
import {
  fetchCurrentSubscription,
  fetchPasskeyAuthOptions,
  fetchSession,
  loginDevelopmentPersona,
  logoutAccount,
  requestLoginOtp,
  verifyLoginOtp,
  verifyPasskeyAuthentication,
} from "../../api/client";
import { liveController } from "../../live/liveController";
import type { VerifyOtpOptions } from "../../api/client";
import type { AccountDevPersona } from "../../api/client";
interface VerifyArgs extends VerifyOtpOptions {
  code: string;
}

export function useAuth(): {
  requestOtp: (email: string, password: string) => Promise<void>;
  verifyOtp: (args: VerifyArgs) => Promise<void>;
  loginWithPasskey: (emailHint?: string) => Promise<void>;
  devLogin: (persona: AccountDevPersona) => Promise<void>;
  loadSubscription: () => Promise<void>;
  logout: () => Promise<void>;
  goToLoggedOut: () => void;
} {
  const patch = useStore((s) => s.patch);
  const setLoading = useStore((s) => s.setLoading);
  const resetSession = useStore((s) => s.resetSession);

  /* Step 1: request OTP (handles the trusted-device fast path). */
  const requestOtp = useCallback(
    async (email: string, password: string) => {
      const result = await requestLoginOtp(email, password);
      patch({ pendingEmail: email });

      if (result?.mode === "trusted_device" && result?.session) {
        const session = await fetchSession();
        const subscription = await fetchCurrentSubscription();
        patch({
          sessionUser: session?.user ?? null,
          subscription,
          authState: "logged-in",
        });
        return;
      }

      patch({ authState: "otp-pending" });
    },
    [patch]
  );

  /* Step 2: verify OTP and move to logged-in state. */
  const verifyOtp = useCallback(
    async ({ code, markTrusted, trustedDeviceLabel }: VerifyArgs) => {
      const pendingEmail = useStore.getState().pendingEmail;
      const result = await verifyLoginOtp(pendingEmail, code, { markTrusted, trustedDeviceLabel });
      const subscription = await fetchCurrentSubscription();
      patch({ sessionUser: result?.user ?? null, subscription, authState: "logged-in" });
    },
    [patch]
  );

  const devLogin = useCallback(
    async (persona: AccountDevPersona) => {
      const result = await loginDevelopmentPersona(persona);
      const subscription = await fetchCurrentSubscription();
      patch({ sessionUser: result?.user ?? null, subscription, authState: "logged-in" });
    },
    [patch]
  );

  /*
   * Passkey authentication flow using @simplewebauthn/browser.
   * Retrieves challenge options from /api/auth/passkeys/authentication/options,
   * invokes navigator.credentials via startAuthentication, and verifies the
   * credential assertion with /api/auth/passkeys/authentication/verify.
   */
  const loginWithPasskey = useCallback(
    async (emailHint?: string): Promise<void> => {
      if (!browserSupportsWebAuthn()) {
        throw new Error("WEBAUTHN_NOT_SUPPORTED");
      }

      const authOptionsData = await fetchPasskeyAuthOptions(emailHint);
      if (!authOptionsData?.options || !authOptionsData.challengeId) {
        throw new Error("NO_PASSKEY_OPTIONS");
      }

      let authResponse: AuthenticationResponseJSON;
      try {
        authResponse = await startAuthentication({ optionsJSON: authOptionsData.options });
      } catch (err: unknown) {
        if (
          err instanceof Error &&
          (err.name === "NotAllowedError" ||
            err.name === "AbortError" ||
            (err instanceof WebAuthnError && err.code === "ERROR_CEREMONY_ABORTED"))
        ) {
          throw new Error("WEBAUTHN_CANCELLED");
        }
        if (err instanceof Error && err.name === "SecurityError") {
          throw new Error("WEBAUTHN_SECURITY_RESTRICTED");
        }
        if (err instanceof Error && err.name === "NotSupportedError") {
          throw new Error("WEBAUTHN_NOT_SUPPORTED");
        }
        throw err;
      }

      const result = await verifyPasskeyAuthentication(authOptionsData.challengeId, authResponse);
      const subscription = await fetchCurrentSubscription();
      patch({
        sessionUser: result?.user ?? null,
        subscription,
        authState: "logged-in",
      });
    },
    [patch]
  );

  const loadSubscription = useCallback(async () => {
    const subscription = await fetchCurrentSubscription();
    patch({ subscription });
  }, [patch]);

  const logout = useCallback(async () => {
    setLoading(true);
    liveController.stop(); // tear down any running live polling before clearing state
    try {
      await logoutAccount();
    } catch {
      /* Ignore backend errors — clear local state regardless. */
    } finally {
      resetSession(); // resets all session/book state to logged-out, loading=false
    }
  }, [setLoading, resetSession]);

  const goToLoggedOut = useCallback(() => {
    patch({ authState: "logged-out" });
  }, [patch]);

  return {
    requestOtp,
    verifyOtp,
    devLogin,
    loadSubscription,
    logout,
    goToLoggedOut,
    loginWithPasskey,
  };
}
